import { Action, Thunk, action, thunk } from "easy-peasy";
import { post, get, del } from "requests";

export interface SyndicationStoreModel {
  applicationId: string | null;
  investorsList: any[];
  cachedSyndicationDistribution: any[];
  syndicationDistribution: any[];
  cachedDeletedSyndicator: any[];
  displayAlert: any;
  errors: string[];
  busyStatus: boolean;
  isEditMode: boolean;
  syndicationPercentageTotal: number;
  fundedTotal: number;
  newSyndicatorName: string;
  syndicatorCurrentCashBalance: any | null;
  isEditDisabled: boolean;
  displayBankFeeAlert: boolean;
  percentageChangedPartyIds: any[];
  investmentPercentageError: boolean;

  setSyndicationDistribution: Action<SyndicationStoreModel, any[]>;
  setCachedSyndicationDistribution: Action<SyndicationStoreModel, any[]>;
  setSyndicatorCurrentCashBalance: Action<SyndicationStoreModel, any>;

  setTotalSumForSyndicationFields: Action<SyndicationStoreModel, any[]>;
  setLoading: Action<SyndicationStoreModel, boolean>;
  setDisplayAlert: Action<SyndicationStoreModel, any>;
  setInvestmentPercentageError: Action<SyndicationStoreModel, any>;

  setError: Action<SyndicationStoreModel, string>;
  setInvestorList: Action<SyndicationStoreModel, any>;

  setIsEditMode: Action<SyndicationStoreModel, boolean>;
  resetStore: Action<SyndicationStoreModel>;
  save: Thunk<
    SyndicationStoreModel,
    { callBack: any; syndication: any; applicationId: any; deletingIds: any[] }
  >;
  beforeSave: Thunk<SyndicationStoreModel, { cachedSyndicationDistribution: any }>;

  fetchSyndicators: Thunk<SyndicationStoreModel, string>;
  fetchInvestorParties: Thunk<SyndicationStoreModel, string>;
  saveSyndicators: Thunk<
    SyndicationStoreModel,
    { cachedSyndicationDistribution: any; deletedSyndicators: any }
  >;
  updateCachedSyndicatorsOfDealApplication: Thunk<
    SyndicationStoreModel,
    { applicationId: any; syndicators: any }
  >;
  updateSyndicatorsPercentage: Thunk<SyndicationStoreModel, { applicationId: any; reload: any }>;
  deleteSyndicator: Thunk<
    SyndicationStoreModel,
    { dealApplicationId: string; syndicatorId: string }
  >;
  onInit: Thunk<SyndicationStoreModel, string>;
}

const SyndicationStore: SyndicationStoreModel = {
  applicationId: null,
  investorsList: [],
  cachedSyndicationDistribution: [],
  syndicationDistribution: [],
  cachedDeletedSyndicator: [],
  displayAlert: undefined,
  errors: [],
  busyStatus: false,
  isEditMode: false,
  syndicationPercentageTotal: 0,
  fundedTotal: 0,
  newSyndicatorName: "",
  syndicatorCurrentCashBalance: null,
  isEditDisabled: false,
  displayBankFeeAlert: false,
  percentageChangedPartyIds: [],
  investmentPercentageError: false,

  setSyndicationDistribution: action((state, payload) => {
    state.syndicationDistribution = payload;
  }),

  setCachedSyndicationDistribution: action((state, payload) => {
    state.cachedSyndicationDistribution = payload;
  }),

  setSyndicatorCurrentCashBalance: action((state, payload) => {
    state.syndicatorCurrentCashBalance = payload;
  }),

  setInvestmentPercentageError: action((state, payload) => {
    state.investmentPercentageError = payload;
  }),

  setInvestorList: action((state, payload) => {
    state.investorsList = payload;
  }),

  setTotalSumForSyndicationFields: action((state, cachedSyndicationDistribution) => {
    let fundedTotal = 0;
    let syndicationPercentageTotal = 0;

    cachedSyndicationDistribution.forEach((syndication) => {
      fundedTotal += syndication.fundedAmount ? parseFloat(syndication.fundedAmount) : 0;
      syndicationPercentageTotal += syndication.investmentPercentage
        ? parseFloat(syndication.investmentPercentage)
        : 0;
    });

    state.fundedTotal = fundedTotal;
    state.syndicationPercentageTotal = syndicationPercentageTotal;
  }),

  setLoading: action((state, payload) => {
    state.busyStatus = payload;
  }),

  setDisplayAlert: action((state, payload) => {
    state.displayAlert = payload;
  }),

  setError: action((state, payload) => {
    state.errors.push(payload);
  }),

  setIsEditMode: action((state, payload) => {
    state.isEditMode = payload;
  }),

  resetStore: action((state) => {
    state.syndicationDistribution = [];
    state.cachedSyndicationDistribution = [];
    state.cachedDeletedSyndicator = [];
    state.errors = [];
    state.busyStatus = false;
    state.isEditMode = false;
    state.syndicationPercentageTotal = 0;
    state.fundedTotal = 0;
    state.syndicatorCurrentCashBalance = null;
    state.newSyndicatorName = "";
  }),

  fetchSyndicators: thunk(async (actions, dealApplicationId, { getState }) => {
    const state = getState();

    try {
      const response = await get(`fetchDealApplicationSyndicators/${dealApplicationId}`);

      actions.setSyndicationDistribution(response.syndicatorList);
      actions.setTotalSumForSyndicationFields(response.syndicatorList);
    } catch (error) {
      actions.setError("Failed to fetch syndicators");
    }
  }),

  fetchInvestorParties: thunk(async (actions, tenantId, { getState }) => {
    const state = getState();
    try {
      const response = await get(`fetchInvestorParties/${tenantId}`);
      const investors = response.investors.map((investor: any) => ({
        name: investor.name,
        fund: investor.availableFund,
        value: investor.name,
        key: investor.id,
      }));
      actions.setInvestorList(investors);
    } catch (error) {
      actions.setError("Failed to fetch investor parties");
    }
  }),

  saveSyndicators: thunk(
    async (actions, { cachedSyndicationDistribution, deletedSyndicators }, { getState }) => {
      const state = getState();
      try {
        const response = await post("saveApplicationSyndication", {
          syndicators: cachedSyndicationDistribution,
          deletedSyndicators: deletedSyndicators,
        });
        return response;
      } catch (error) {
        actions.setError("Failed to save syndicators");
      }
    }
  ),

  updateCachedSyndicatorsOfDealApplication: thunk(
    async (actions, { applicationId, syndicators }, { getState }) => {
      const state = getState();
      try {
        const response = await post("updateCachedSyndicatorsOfDealApplication", {
          applicationId: applicationId,
          syndicators: syndicators,
        });
        // actions.setTotalSumForSyndicationFields(response);
        return response;
      } catch (error) {
        actions.setError("Failed to update syndicator's percentage");
        return {};
      }
    }
  ),

  updateSyndicatorsPercentage: thunk(async (actions, { applicationId, reload }, { getState }) => {
    const state = getState();
    try {
      await post("updateSyndicatorsPercentageOfDealApplication", {
        applicationId: applicationId,
      });
      if (reload) {
        await actions.fetchSyndicators(applicationId);
      }
    } catch (error) {
      actions.setError("Failed to update syndicator's percentage");
    }
  }),

  deleteSyndicator: thunk(async (actions, { dealApplicationId, syndicatorId }, { getState }) => {
    const state = getState();
    try {
      await del(`deleteSyndicator/${dealApplicationId}/${syndicatorId}`);
      actions.resetStore();
      await actions.fetchSyndicators(dealApplicationId);
    } catch (error) {
      actions.setError("Failed to delete syndicator");
    }
  }),

  onInit: thunk(async (actions, dealApplicationId, { getState }) => {
    actions.resetStore();
    await actions.fetchSyndicators(dealApplicationId);
    await actions.fetchInvestorParties("1");
  }),

  beforeSave: thunk(async (actions, { cachedSyndicationDistribution }) => {
    let newSyndicatorPartyList: any = [];
    let dist: any = [];

    cachedSyndicationDistribution.forEach((dis: any) => {
      if (dis && dis.newSyndicatorParty) newSyndicatorPartyList.push(dis.newSyndicatorParty);
      if (
        parseFloat(dis["investmentPercentage"]) &&
        (dis["partyId"] || dis["addNewSyndicatorParty"])
      ) {
        dist.push(dis);
      }
    });

    cachedSyndicationDistribution = dist;

    const hasDuplicates = new Set(newSyndicatorPartyList).size !== newSyndicatorPartyList.length;

    if (!hasDuplicates) {
      return cachedSyndicationDistribution;
    }
    return null;
  }),

  save: thunk(
    async (actions, { callBack, applicationId, syndication, deletingIds }, { getState }) => {
      const state = getState();
      try {
        const cleanedSyndication = await actions.beforeSave({
          cachedSyndicationDistribution: syndication,
        });
        const response = await actions.saveSyndicators({
          cachedSyndicationDistribution: cleanedSyndication,
          deletedSyndicators: deletingIds,
        });

        if (response) {
          if (response["syndicationPercentageIssue"]) {
            actions.setInvestmentPercentageError(true);
          } else {
            const syndicatorsData = response["syndicatorsData"];
            await actions.updateSyndicatorsPercentage({
              applicationId: applicationId,
              reload: true,
            });
            if (callBack) callBack();
          }
        }
      } catch (error) {
        actions.setError("Failed to save data");
      }
    }
  ),
};

export default SyndicationStore;
